import React, { useMemo } from 'react';
import { StarFull, StarHalf, StarEmpty } from '../../components/Icons';

import styles from '../../components/Stars/Stars.module.scss';

type Props = {
  /**
   * Current value.
   */
  value?: number;
  /**
   * Amount of stars that will be drawn.
   */
  amount?: number;
  /**
   * Size of stars.
   */
  size?: 'smallest' | 'small' | 'large';
  /**
   * Stars color.
   */
  color?: 'primary' | 'secondary' | 'tertiary';
  /**
   * Show the value.
   */
  showValue?: boolean;
};

export const Stars: React.FC<Props> = ({ value = 0, amount = 5, size = 'small', color = 'primary', showValue }) => {
  const curValue = useMemo(() => value, [value]);
  const intValue = useMemo(() => Math.floor(curValue), [curValue]);
  const formattedValue = useMemo(() => curValue.toFixed(1), [curValue]);

  const stars = useMemo(
    () =>
      [...new Array(amount)].map((v, i) => {
        if (i < intValue) {
          return (
            <span key={`star_${i}`} className={styles.star}>
              <StarFull />
            </span>
          );
        } else if (intValue !== curValue && i < curValue) {
          return (
            <span key={`star_${i}`} className={styles.star}>
              <StarHalf />
            </span>
          );
        }

        return (
          <span key={`star_${i}`} className={styles.star}>
            <StarEmpty />
          </span>
        );
      }),
    [amount, curValue, intValue],
  );

  return (
    <span className={`${styles['stars__container']} ${styles['stars--' + size]} ${styles['stars--' + color]}`}>
      {stars}
      {showValue && <span className={styles['star-value']}>{formattedValue}</span>}
    </span>
  );
};
